import type React from 'react';
import BooleanField from './fields/BooleanField';
import ChoiceField from './fields/ChoiceField';
import InputField from './fields/InputField';
import MarkdownField from './fields/MarkdownField/MarkdownField';
import RowsField from '../pages/reportBuilder/forms/fields/RowsField';
import StringListField from '../pages/reportBuilder/forms/fields/StringListField';
import SwarmTypesField from '../pages/reportBuilder/forms/fields/SwarmTypesField';
import GroupField from './fields/GroupField';
import {
    FieldComponentResolverContext,
    type FormFieldComponent,
} from './fieldComponentResolverContext';

type FormFieldOverrides = Record<string, FormFieldComponent>;

const formFieldComponents: Record<string, FormFieldComponent> = {
    char: InputField as FormFieldComponent,
    markdown: MarkdownField as FormFieldComponent,
    integer: InputField as FormFieldComponent,
    float: InputField as FormFieldComponent,
    choice: ChoiceField as FormFieldComponent,
    boolean: BooleanField as FormFieldComponent,
    rows: RowsField as FormFieldComponent,
    stringList: StringListField as FormFieldComponent,
    swarmTypes: SwarmTypesField as FormFieldComponent,
    group: GroupField as FormFieldComponent,
};

function resolveFormFieldComponent(
    fieldType: string,
    fieldName: string,
    formFieldOverrides?: FormFieldOverrides
): FormFieldComponent {
    const byName = formFieldOverrides?.[fieldName];
    if (byName) {
        return byName;
    }

    const FieldComponent = formFieldComponents[fieldType];
    if (!FieldComponent) {
        throw new Error(`Could not find field component for ${fieldName} of type ${fieldType}`);
    }

    return FieldComponent;
}

export function FieldComponentRegistryProvider({
    formFieldOverrides,
    children,
}: {
    formFieldOverrides?: FormFieldOverrides;
    children: React.ReactNode;
}): JSX.Element {
    function resolveFieldComponent(fieldType: string, fieldName: string): FormFieldComponent {
        return resolveFormFieldComponent(fieldType, fieldName, formFieldOverrides);
    }

    return (
        <FieldComponentResolverContext.Provider value={resolveFieldComponent}>
            {children}
        </FieldComponentResolverContext.Provider>
    );
}
